import React, { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, TrendingUp, AlertTriangle, Shield, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

interface SocialPost {
  id: string;
  platform: string;
  text: string;
  author: string;
  author_id: string;
  author_verified: boolean;
  author_followers: number;
  timestamp: string;
  hashtags: string[];
  mentions: string[];
  likes: number;
  shares: number;
  comments: number;
  views: number;
  sentiment: 'positive' | 'negative' | 'neutral' | 'unknown';
  url: string;
  metadata: Record<string, any>;
}

interface AccountProfile {
  authorId: string;
  name: string;
  platform: string;
  verified: boolean;
  followers: number;
  posts: SocialPost[];
  postCount: number;
  avgEngagement: number;
  engagementRate: number;
  hashtagDiversity: number;  // unique hashtags / total hashtags used
  sentimentVariance: number; // 0 = always same, 1 = very varied
  postingFrequency: number;  // posts per hour
  dominantSentiment: string;
  botScore: number;
  clusterLabel: 'influencer' | 'organic' | 'suspicious';
  clusterReason: string;
}

function computeAccountProfiles(posts: SocialPost[]): AccountProfile[] {
  const authorMap = new Map<string, SocialPost[]>();

  posts.forEach(post => {
    const key = post.author_id || post.author;
    if (!authorMap.has(key)) authorMap.set(key, []);
    authorMap.get(key)!.push(post);
  });

  return Array.from(authorMap.entries()).map(([authorId, authorPosts]) => {
    const first = authorPosts[0];
    const followers = first.author_followers || 0;
    const totalEngagement = authorPosts.reduce((s, p) => s + p.likes + p.shares + p.comments, 0);
    const avgEngagement = totalEngagement / authorPosts.length;
    const engagementRate = followers > 0 ? avgEngagement / followers : 0;

    // Hashtag diversity: unique tags / total tag usage
    const allTags = authorPosts.flatMap(p => p.hashtags || []).map(t => t.toLowerCase());
    const uniqueTags = new Set(allTags).size;
    const hashtagDiversity = allTags.length > 0 ? uniqueTags / allTags.length : 0;

    // Sentiment variance: how mixed the sentiments are
    const sentimentCounts = authorPosts.reduce((acc, p) => {
      acc[p.sentiment] = (acc[p.sentiment] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const sentimentValues = Object.values(sentimentCounts);
    const total = sentimentValues.reduce((a, b) => a + b, 0);
    const dominantSentiment = Object.entries(sentimentCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
    // Entropy-based variance (normalized)
    const entropy = sentimentValues.reduce((acc, v) => {
      const p = v / total;
      return acc - (p > 0 ? p * Math.log2(p) : 0);
    }, 0);
    const sentimentVariance = Math.min(1, entropy / Math.log2(4)); // normalize by max entropy (4 classes)

    // Posting frequency: posts per hour across the time window
    let postingFrequency = 0;
    if (authorPosts.length > 1) {
      const times = authorPosts.map(p => new Date(p.timestamp).getTime()).sort();
      const spanHours = (times[times.length - 1] - times[0]) / 3_600_000;
      postingFrequency = spanHours > 0 ? authorPosts.length / spanHours : authorPosts.length;
    }

    // Bot scoring heuristics
    let botScore = 0;
    const reasons: string[] = [];

    // 1. Very low engagement rate with significant followers → suspicious
    if (followers > 5000 && engagementRate < 0.0003) { botScore += 0.35; reasons.push('low engagement rate'); }
    else if (followers > 1000 && engagementRate < 0.001) { botScore += 0.15; }

    // 2. Round follower counts (bot farms often have bought followers)
    if (followers > 0 && followers % 1000 === 0 && !first.author_verified) { botScore += 0.1; }

    // 3. Very high posting frequency
    if (postingFrequency > 10) { botScore += 0.35; reasons.push('very high post rate'); }
    else if (postingFrequency > 5) { botScore += 0.2; reasons.push('high post rate'); }

    // 4. Zero hashtag diversity (always same hashtags = coordinated campaign)
    if (allTags.length > 3 && hashtagDiversity < 0.2) { botScore += 0.2; reasons.push('repetitive hashtags'); }

    // 5. Always same sentiment (no natural variation)
    if (authorPosts.length > 5 && sentimentVariance < 0.1) { botScore += 0.1; reasons.push('uniform sentiment'); }

    // 6. No followers but high volume posts
    if (followers === 0 && authorPosts.length > 5) { botScore += 0.25; reasons.push('no followers + high volume'); }

    botScore = Math.min(1, botScore);

    // Cluster assignment
    let clusterLabel: AccountProfile['clusterLabel'];
    let clusterReason: string;

    if (botScore >= 0.5) {
      clusterLabel = 'suspicious';
      clusterReason = reasons.length > 0 ? reasons.join(', ') : 'multiple suspicious signals';
    } else if (followers >= 10000 || (engagementRate > 0.02 && followers >= 1000) || first.author_verified) {
      clusterLabel = 'influencer';
      clusterReason = first.author_verified ? 'verified account' : followers >= 10000 ? 'large following' : 'high engagement rate';
    } else {
      clusterLabel = 'organic';
      clusterReason = 'normal activity patterns';
    }

    return {
      authorId,
      name: first.author,
      platform: first.platform,
      verified: first.author_verified,
      followers,
      posts: authorPosts,
      postCount: authorPosts.length,
      avgEngagement,
      engagementRate,
      hashtagDiversity,
      sentimentVariance,
      postingFrequency,
      dominantSentiment,
      botScore,
      clusterLabel,
      clusterReason,
    };
  });
}

const PLATFORM_COLORS: Record<string, string> = {
  twitter: '#1DA1F2', x: '#000000', instagram: '#E1306C',
  facebook: '#1877F2', tiktok: '#010101', reddit: '#FF4500',
  youtube: '#FF0000', telegram: '#2CA5E0', default: '#6366F1',
};

const CLUSTER_CONFIG = {
  influencer: { label: 'Influencers', icon: TrendingUp, color: 'text-blue-400', badgeClass: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  organic: { label: 'Organic Users', icon: Users, color: 'text-green-400', badgeClass: 'bg-green-500/20 text-green-400 border-green-500/30' },
  suspicious: { label: 'Suspicious Accounts', icon: AlertTriangle, color: 'text-red-400', badgeClass: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

interface AccountClustersProps {
  posts: SocialPost[];
  onAccountClick?: (account: AccountProfile) => void;
}

const AccountClusters: React.FC<AccountClustersProps> = ({ posts, onAccountClick }) => {
  const [expandedCluster, setExpandedCluster] = useState<string | null>('suspicious');
  const [sortBy, setSortBy] = useState<'botScore' | 'followers' | 'engagement' | 'postCount'>('botScore');
  const [filterPlatform, setFilterPlatform] = useState('all');

  const profiles = useMemo(() => computeAccountProfiles(posts), [posts]);

  const filteredProfiles = useMemo(() =>
    filterPlatform === 'all' ? profiles : profiles.filter(p => p.platform === filterPlatform),
    [profiles, filterPlatform]
  );

  const clusters = useMemo(() => {
    const byCluster: Record<string, AccountProfile[]> = { influencer: [], organic: [], suspicious: [] };
    filteredProfiles.forEach(p => byCluster[p.clusterLabel].push(p));
    // Sort within each cluster
    Object.values(byCluster).forEach(list =>
      list.sort((a, b) => {
        if (sortBy === 'botScore') return b.botScore - a.botScore;
        if (sortBy === 'followers') return b.followers - a.followers;
        if (sortBy === 'engagement') return b.avgEngagement - a.avgEngagement;
        return b.postCount - a.postCount;
      })
    );
    return byCluster;
  }, [filteredProfiles, sortBy]);

  const platforms = [...new Set(posts.map(p => p.platform))];

  const overallBotPercent = profiles.length > 0
    ? Math.round(profiles.filter(p => p.clusterLabel === 'suspicious').length / profiles.length * 100)
    : 0;

  if (posts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <div className="text-center">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No accounts to analyze</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
          <div className="text-2xl font-bold">{profiles.length}</div>
          <div className="text-xs text-muted-foreground mt-1">Unique accounts</div>
        </div>
        <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
          <div className="text-2xl font-bold text-blue-400">{clusters.influencer.length}</div>
          <div className="text-xs text-muted-foreground mt-1">Influencers</div>
        </div>
        <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
          <div className="text-2xl font-bold text-green-400">{clusters.organic.length}</div>
          <div className="text-xs text-muted-foreground mt-1">Organic users</div>
        </div>
        <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
          <div className={`text-2xl font-bold ${overallBotPercent > 20 ? 'text-red-400' : 'text-yellow-400'}`}>
            {overallBotPercent}%
          </div>
          <div className="text-xs text-muted-foreground mt-1">Suspicious rate</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filterPlatform} onValueChange={setFilterPlatform}>
          <SelectTrigger className="w-32 h-8 text-xs" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
            <SelectItem value="all" style={{ color: '#e5e7eb' }}>All Platforms</SelectItem>
            {platforms.map(p => <SelectItem key={p} value={p} style={{ color: '#e5e7eb' }} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={v => setSortBy(v as any)}>
          <SelectTrigger className="w-40 h-8 text-xs" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
            <SelectItem value="botScore" style={{ color: '#e5e7eb' }}>Sort: Bot Score</SelectItem>
            <SelectItem value="followers" style={{ color: '#e5e7eb' }}>Sort: Followers</SelectItem>
            <SelectItem value="engagement" style={{ color: '#e5e7eb' }}>Sort: Engagement</SelectItem>
            <SelectItem value="postCount" style={{ color: '#e5e7eb' }}>Sort: Post Count</SelectItem>
          </SelectContent>
        </Select>

        {overallBotPercent > 25 && (
          <div className="flex items-center gap-1.5 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2 py-1">
            <AlertTriangle className="w-3 h-3" />
            High suspicious account rate detected — possible coordinated campaign
          </div>
        )}
      </div>

      {/* Cluster sections */}
      {(['suspicious', 'influencer', 'organic'] as const).map(clusterKey => {
        const config = CLUSTER_CONFIG[clusterKey];
        const Icon = config.icon;
        const accounts = clusters[clusterKey];
        const isExpanded = expandedCluster === clusterKey;

        return (
          <div key={clusterKey} className="border border-slate-800 rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 bg-slate-900/50 hover:bg-slate-900 transition-colors"
              onClick={() => setExpandedCluster(isExpanded ? null : clusterKey)}
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-4 h-4 ${config.color}`} />
                <span className="font-medium text-sm">{config.label}</span>
                <Badge variant="outline" className={config.badgeClass}>
                  {accounts.length} accounts
                </Badge>
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>

            {isExpanded && accounts.length > 0 && (
              <div className="divide-y divide-slate-800/50">
                {accounts.slice(0, 20).map(account => (
                  <div
                    key={account.authorId}
                    className="p-4 hover:bg-slate-900/30 cursor-pointer transition-colors"
                    onClick={() => onAccountClick?.(account)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Avatar placeholder */}
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{ backgroundColor: PLATFORM_COLORS[account.platform] || PLATFORM_COLORS.default }}
                      >
                        {account.name.charAt(0).toUpperCase()}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">{account.name}</span>
                          {account.verified && <Shield className="w-3 h-3 text-blue-400 shrink-0" />}
                          <Badge
                            variant="outline"
                            className="text-[10px] shrink-0"
                            style={{ borderColor: PLATFORM_COLORS[account.platform], color: PLATFORM_COLORS[account.platform] }}
                          >
                            {account.platform}
                          </Badge>
                          {account.posts[0]?.url && (
                            <a
                              href={account.posts[0].url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{account.followers.toLocaleString()} followers</span>
                          <span>{account.postCount} posts</span>
                          <span>{account.avgEngagement.toFixed(1)} avg. engagement</span>
                          <span className="capitalize">{account.dominantSentiment} sentiment</span>
                        </div>

                        {clusterKey === 'suspicious' && (
                          <div className="mt-1 text-xs text-red-400/80">
                            Signals: {account.clusterReason}
                          </div>
                        )}
                      </div>

                      {/* Bot score meter */}
                      <div className="w-20 shrink-0 text-right">
                        <div className="text-xs text-muted-foreground mb-1">
                          {clusterKey === 'suspicious' ? 'Bot score' : 'Score'}
                        </div>
                        <div className="text-sm font-bold"
                          style={{
                            color: account.botScore > 0.5 ? '#ef4444' : account.botScore > 0.25 ? '#eab308' : '#22c55e'
                          }}
                        >
                          {(account.botScore * 100).toFixed(0)}%
                        </div>
                        <div className="w-full bg-slate-800 rounded-full h-1 mt-1">
                          <div
                            className="h-1 rounded-full"
                            style={{
                              width: `${account.botScore * 100}%`,
                              backgroundColor: account.botScore > 0.5 ? '#ef4444' : account.botScore > 0.25 ? '#eab308' : '#22c55e',
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Mini engagement bars */}
                    <div className="mt-2 grid grid-cols-4 gap-2 text-[10px] text-muted-foreground">
                      <div>
                        <div className="text-foreground font-medium">{account.posts.reduce((s, p) => s + p.likes, 0).toLocaleString()}</div>
                        <div>likes</div>
                      </div>
                      <div>
                        <div className="text-foreground font-medium">{account.posts.reduce((s, p) => s + p.shares, 0).toLocaleString()}</div>
                        <div>shares</div>
                      </div>
                      <div>
                        <div className="text-foreground font-medium">{account.posts.reduce((s, p) => s + p.comments, 0).toLocaleString()}</div>
                        <div>comments</div>
                      </div>
                      <div>
                        <div className="text-foreground font-medium">{(account.engagementRate * 100).toFixed(2)}%</div>
                        <div>eng. rate</div>
                      </div>
                    </div>
                  </div>
                ))}
                {accounts.length > 20 && (
                  <div className="p-3 text-center text-xs text-muted-foreground">
                    +{accounts.length - 20} more accounts not shown
                  </div>
                )}
              </div>
            )}

            {isExpanded && accounts.length === 0 && (
              <div className="p-6 text-center text-xs text-muted-foreground">
                No accounts in this cluster
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AccountClusters;
